import _                         from 'underscore';
import classNames                from 'classnames';
import React, { FC, useContext } from 'react';
import { useFormikContext }      from 'formik';
import { Cell, Grid }            from 'styled-css-grid';
import { Button, Popup }         from 'semantic-ui-react';

import { CorvalProjectActivity }                                                     from '../../../../types';
import { formatCurrency }                                                            from '../../../../util';
import { useAuditRecords, useFeatureFlags }                                          from '../../../../hooks';
import { ResourcesService }                                                          from '../../../../services';
import { AUDIT_RECORD_TYPE }                                                         from '../../../../constants/SystemRecordEnums';
import { FieldDescription, FieldWrapper, TextField }                                 from '../../../components/forms/formik';
import { FinePrintMessage }                                                          from '../../../components/util/messaging/FinePrintMessage';
import { FieldLabel }                                                                from '../../../components/forms/formik/FieldLabel';
import { AuditMemo, AuditMemoStatement }                                             from '../../../components/audits/AuditMemo';
import { ProjectActivityUpdateDialogContext, ProjectActivityUpdateDialogFormValues } from './ProjectActivityUpdateDialog';

export interface ProjectActivityUpdateAreaCostDisplayOptions {
	showOperationalCostOverride?: boolean,
	showOperationalCostOverrideAudit?: boolean,
	showOngoingCostOverride?: boolean,
	showOngoingCostOverrideAudit?: boolean,
}

interface Props extends ProjectActivityUpdateAreaCostDisplayOptions {}

const defaultProps: Props = {
	showOperationalCostOverride:      true,
	showOperationalCostOverrideAudit: false,
	showOngoingCostOverride:          true,
	showOngoingCostOverrideAudit:     false,
}

const ProjectActivityUpdateAreaCost: FC<Props> = ( props ) => {

	let { showOperationalCostOverride, showOngoingCostOverride }           = props,
	    { showOperationalCostOverrideAudit, showOngoingCostOverrideAudit } = props,
	    { projectActivity, isLoading }                                     = useContext( ProjectActivityUpdateDialogContext ),
	    form                                                               = useFormikContext<ProjectActivityUpdateDialogFormValues>(),
	    featureFlags                                                       = useFeatureFlags();

	let [ originalOperationalCost, , ]      = ResourcesService.getTotalInitialActivityFees( projectActivity, false ),
	    [ minOngoingCost, maxOngoingCost, ] = ResourcesService.getTotalOngoingActivityCosts( projectActivity, false ),
	    originalOngoingCost                 = ResourcesService.getCostValueFromCostData( [ minOngoingCost, maxOngoingCost ] ),
	    hasOperationalCostOverride          = ( typeof form.values.operationalCostOverride == 'number' || !_.isEmpty( form.values.operationalCostOverrideNote ) ),
	    hasOngoingCostOverride              = ( typeof form.values.ongoingCostOverride == 'number' || !_.isEmpty( form.values.ongoingCostOverrideNote ) );

	const handleResetInitialCost = () => {

		form.setFieldValue( 'operationalCostOverride', '', true );
		form.setFieldValue( 'operationalCostOverrideNote', '', true );

		form.setFieldTouched( 'operationalCostOverride', true, true );
		form.setFieldTouched( 'operationalCostOverrideNote', true, true );
	};

	const handleResetOngoingCost = () => {

		form.setFieldValue( 'ongoingCostOverride', '', true );
		form.setFieldValue( 'ongoingCostOverrideNote', '', true );

		form.setFieldTouched( 'ongoingCostOverride', true, true );
		form.setFieldTouched( 'ongoingCostOverrideNote', true, true );
	};

	let canEditOpCost      = featureFlags.enableProjectActivityOperationalCostEditing,
	    canEditOngoingCost = featureFlags.enableProjectActivityOngoingCostEditing;

	return (

		<Grid columns={ 'auto' } rows={ 'repeat(2,auto)' } rowGap={ '20px' }>

			{/* Initial Cost */ }
			{ showOperationalCostOverride && (
				<Cell>
					<FieldWrapper>
						<FieldLabel>Initial Cost</FieldLabel>
						<FieldDescription>
							<FinePrintMessage as={ 'span' }>The initial cost for this activity to be implemented or enacted. Provide a note to help your team understand why any custom value you provide is different than Corval's.</FinePrintMessage>
						</FieldDescription>
					</FieldWrapper>

					<CostControlGrid hasRemoveColumn={ canEditOpCost ? hasOperationalCostOverride : false }>

						<Cell>

							<TextField
								readOnly
								type='text'
								label={ `Corval's Cost` }
								name={ 'originalOperationalCost' }
								value={ formatCurrency( originalOperationalCost ) }
								input={ { className: classNames( 'border--colored-purple', 'cursor--default' ) } }
								disabled={ isLoading }
							/>

						</Cell>
						<Cell>

							<TextField
								autoFocus={ true }
								type='number'
								label={ 'Custom Cost' }
								name={ 'operationalCostOverride' }
								placeholder='0.00'
								readOnly={ !canEditOpCost }
								input={ { className: classNames( { 'cursor--default': !canEditOpCost } ) } }
								disabled={ isLoading }
								loading={ isLoading }/>
						</Cell>
						<Cell>

							<TextField
								type='text'
								label={ 'Note' }
								readOnly={ !canEditOpCost }
								input={ { className: classNames( { 'cursor--default': !canEditOpCost } ) } }
								disabled={ isLoading }
								name={ 'operationalCostOverrideNote' }
								placeholder='Describe the custom value, eg: "Partner discount"'
								loading={ isLoading }/>

						</Cell>

						<ResetItemCell show={ canEditOpCost && !isLoading && hasOperationalCostOverride } onClick={ handleResetInitialCost }/>

					</CostControlGrid>

					{ showOperationalCostOverrideAudit &&
					<CostOverrideAuditEntryCell
						projectActivity={ projectActivity }
						auditEntryFacet={ 'operationalCost' }
						originalCost={ originalOperationalCost }
					/> }

				</Cell>
			) }

			{/* Ongoing Cost */ }
			{ showOngoingCostOverride && (
				<Cell>
					<FieldWrapper>
						<FieldLabel>Ongoing Quarterly Cost</FieldLabel>
						<FieldDescription>
							<FinePrintMessage as={ 'span' }>
								<strong className={ 'display--block text--colored-warning' }>Note: Updates to this cost affects all applicable quarters.</strong>
								{ ' ' }
								{ featureFlags.useDEMSpecificOngoingCostDuration
								  ? `Your custom value for the average cost per quarter to maintain this activity, applied to a number of subsequent quarters according to Corval's specified ongoing cost duration for this activity.`
								  : `Your custom value for the average cost per quarter to maintain this activity, applied each subsequent quarter until the project ends.` }
								{ ' ' }
								Provide a note to help your team understand why any custom value you provide is different than Corval's.
							</FinePrintMessage>
						</FieldDescription>
					</FieldWrapper>

					<CostControlGrid hasRemoveColumn={ canEditOngoingCost ? hasOngoingCostOverride : false }>

						<Cell>

							<TextField
								readOnly
								type='text'
								label={ `Corval's Cost` }
								name={ 'originalOngoingCost' }
								input={ { className: classNames( 'border--colored-purple', 'cursor--default' ) } }
								value={ `${ formatCurrency( originalOngoingCost ) }` }
								disabled={ isLoading }
							/>

						</Cell>
						<Cell>

							<TextField
								autoFocus={ !props.showOperationalCostOverride }
								type='number'
								label={ 'Custom Cost' }
								name={ 'ongoingCostOverride' }
								placeholder='0.00'
								readOnly={ !canEditOngoingCost }
								input={ { className: classNames( { 'cursor--default': !canEditOngoingCost } ) } }
								disabled={ isLoading }
								loading={ isLoading }/>
						</Cell>
						<Cell>

							<TextField
								type='text'
								label={ 'Note' }
								readOnly={ !canEditOngoingCost }
								input={ { className: classNames( { 'cursor--default': !canEditOngoingCost } ) } }
								disabled={ isLoading }
								name={ 'ongoingCostOverrideNote' }
								placeholder='Describe the custom value, eg: "Partner discount"'
								loading={ isLoading }/>

						</Cell>

						<ResetItemCell show={ canEditOngoingCost && !isLoading && hasOngoingCostOverride } onClick={ handleResetOngoingCost }/>

						{ showOngoingCostOverrideAudit &&
						<CostOverrideAuditEntryCell
							projectActivity={ projectActivity }
							auditEntryFacet={ 'ongoingCost' }
							originalCost={ originalOngoingCost }
						/> }

					</CostControlGrid>

				</Cell>
			) }
		</Grid>
	);
};

const CostControlGrid: FC<{ hasRemoveColumn: boolean }> = ( props ) => {

	return <Grid columns={ `20% 20% auto ${ props.hasRemoveColumn ? '35px' : '0px' }` } rows={ `auto auto` } columnGap={ '18px' }>{ props.children }</Grid>
}

const ResetItemCell: FC<{ show: boolean, onClick(): void }> = ( props ) => {

	return (
		<Cell className={ 'flexbox flexbox flexbox--justify-center flexbox--align-end' }>

			{ props.show && (
				<Popup
					content={ 'Clear custom values' }
					position={ 'top right' }
					size={ 'tiny' }
					trigger={
						<Button basic
						        type={ 'button' }
						        icon={ { name: 'close', fitted: true } }
						        className={ 'no-box-shadow' }
						        onClick={ props.onClick }
						/>
					}/>
			) }

		</Cell>
	)
}

const CostOverrideAuditEntryCell: FC<{
	projectActivity: CorvalProjectActivity,
	auditEntryFacet: 'operationalCost' | 'ongoingCost',
	originalCost?: number,
}> = ( props ) => {

	let { getAuditRecordTypes, getAuditRecordsFromEntity }           = useAuditRecords(),
	    { valuesFromChangeEntryDiffer, extractValueFromChangeEntry } = useAuditRecords();

	let auditRecord = _.first( getAuditRecordTypes( getAuditRecordsFromEntity( props.projectActivity ), [ AUDIT_RECORD_TYPE.PROJECT_ACTIVITY_COST_OVERRIDE_INFO_CHANGED ], 'newest' ) );

	let auditDataKeys: [ costAttribute: string, noteAttribute: string ] = [ null, null ],
	    original                                                        = null;

	switch( props.auditEntryFacet ){

		case 'operationalCost':
			auditDataKeys = [
				'OperationalCostOverride',
				'OperationalCostOverrideNote',
			];
			original      = ResourcesService.getTotalInitialActivityFees( props.projectActivity, false )[ 0 ];
			break;

		case 'ongoingCost':
			auditDataKeys = [
				'OngoingCostOverride',
				'OngoingCostOverrideNote',
			];
			original      = ResourcesService.getCostValueFromCostData( ResourcesService.getTotalOngoingActivityCosts( props.projectActivity, false ) )
			break;
	}

	return (
		<Cell width={ 3 } style={{maxHeight: '40%'}}>

			{/* AUDIT ENTRY MESSAGE FOR COST / NOTE AREA */ }
			<AuditMemo record={ auditRecord }
			           containsAuditDataChangesOn={ auditDataKeys }
			           includeAvatar={ true }
			           render={ ( memoProps, auditEntry ) => {

				           let auditData                        = JSON.parse( auditEntry.auditData ),
				               [ costAttribute, noteAttribute ] = auditDataKeys;

				           // pull out the changes for the specified keys
				           let costChanges  = extractValueFromChangeEntry( auditData[ costAttribute ] ),
				               noteChanges  = extractValueFromChangeEntry( auditData[ noteAttribute ] ),
				               oldCostValue = formatCurrency( costChanges[ 1 ] ?? props.originalCost );

				           // compare the old / new value and craft a subject
				           let costChanged = valuesFromChangeEntryDiffer( costChanges ),
				               noteChanged = valuesFromChangeEntryDiffer( noteChanges ),
				               subject     = ( costChanged && noteChanged )
				                             ? `these details`
				                             : costChanged
				                               ? `the custom cost value`
				                               : noteChanged
				                                 ? 'the note' : '';

				           return (
					           <AuditMemoStatement
						           { ...memoProps }
						           actionDesc={ '' }
						           actionVerb={ 'updated' }
						           auditSubject={ subject }
						           auditAdditionalInfo={ `The previous cost was ${ oldCostValue }.` }
					           />
				           );

			           } }/>

		</Cell>
	)
}

ProjectActivityUpdateAreaCost.defaultProps = defaultProps;

export { ProjectActivityUpdateAreaCost }
